import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { postVisitReservation, getTenantInfo, getUserDetail, getPersonalDetail } from '../services/api';
import {
  Box, Button, FormControl, FormLabel, Input, VStack, Heading, useToast, Container, Select, Textarea, Flex, Text, HStack, Spinner
} from '@chakra-ui/react';

const TIME_SLOTS = [
  '10:00-11:00',
  '11:00-12:00',
  '13:00-14:00',
  '14:00-15:00',
  '15:00-16:00',
  '16:00-17:00',
];

export default function VisitReservation() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [tenantInfo, setTenantInfo] = useState(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: currentUser?.email || '',
    address: '',
    age: '',
    family: '',
    date1: '',
    slot1: '',
    date2: '',
    slot2: '',
    date3: '',
    slot3: '',
    inquiry: '',
  });
  const [loading, setLoading] = useState(false);
  const [isUserInfoLoading, setIsUserInfoLoading] = useState(true);



  // テナント情報を取得
  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const info = await getTenantInfo();
        setTenantInfo(info);
      } catch (error) {
        console.error('テナント情報の取得に失敗しました:', error);
        // デフォルト値を設定
        setTenantInfo({
          display_name: '',
          name: '注文住宅会社',
          address: '',
          contact_phone: '',
          contact_email: ''
        });
      }
    };
    fetchTenantInfo();
  }, []);

  // ユーザー情報を取得してフォームに設定
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!currentUser) {
        setIsUserInfoLoading(false);
        return;
      }
      
      try {
        const userData = await getUserDetail(currentUser.uid);
        
        setForm(prev => ({
          ...prev,
          name: userData.name || '',
          phone: userData.phone || '',
          email: userData.email || '',
          address: userData.address || '',
          age: userData.age ? userData.age.toString() : '',
          family: userData.family || '',
        }));
      } catch (error) {
        console.error('ユーザー情報の取得に失敗しました:', error);
        // エラーの場合はメールアドレスのみ設定
        setForm(prev => ({
          ...prev,
          email: currentUser.email || '',
        }));
      } finally {
        setIsUserInfoLoading(false);
      }
    };
    
    fetchUserInfo();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const isFutureDate = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0,0,0,0);
    const d = new Date(dateStr);
    return d >= today;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // バリデーション
    if (!form.phone || !form.email || !form.address || !form.age || !form.family ||
        !form.name ||
        !form.date1 || !form.slot1 || !form.date2 || !form.slot2 || !form.date3 || !form.slot3 || !form.inquiry) {
      toast({ title: '全ての項目を入力してください', status: 'warning' });
      return;
    }
    if (!/^\d{10,11}$/.test(form.phone)) {
      toast({ title: '電話番号は10〜11桁の数字で入力してください', status: 'warning' });
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast({ title: 'メールアドレスの形式が正しくありません', status: 'warning' });
      return;
    }
    if (isNaN(Number(form.age)) || Number(form.age) <= 0) {
      toast({ title: '年齢は正の数字で入力してください', status: 'warning' });
      return;
    }
    if (![form.date1, form.date2, form.date3].every(isFutureDate)) {
      toast({ title: '希望日は本日以降の日付を選択してください', status: 'warning' });
      return;
    }
    setLoading(true);
    try {
      await postVisitReservation({
        ...form,
        age: Number(form.age),
      });
      toast({ title: '来店予約を送信しました', status: 'success' });
      navigate('/');
    } catch (err) {
      toast({ title: '送信に失敗しました', description: err?.response?.data?.detail || err.message, status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex gap={8} w="100vw" h="100%" direction="column">
      <Box p={4}>
        <Heading size="md">来店予約</Heading>
       <Box 
            bg="gray.100"
            p={4}
            borderRadius="md"
            w="100%"
        >
        <Text>
          来店予約ができます。ご気軽にお申込みください！
        </Text>
    </Box>
    
    {isUserInfoLoading ? (
      <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="lg" />
      </Box>
    ) : (
    <Box minH="100vh" bg="gray.50" py={10} display="flex" alignItems="center" justifyContent="center">
      <Container maxW="1200px">
        {/* 会社情報・アクセス・GoogleMap */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
          <Heading size="md" mb={2}>{tenantInfo?.display_name || ''}</Heading>
          <Text fontWeight="bold">住所：</Text>
          <Text mb={2}>{tenantInfo?.address || ''}</Text>
          {tenantInfo?.contact_phone && (
            <>
              <Text fontWeight="bold">電話番号：</Text>
              <Text mb={2}>{tenantInfo.contact_phone}</Text>
            </>
          )}
          {tenantInfo?.contact_email && (
            <>
              <Text fontWeight="bold">メールアドレス：</Text>
              <Text mb={2}>{tenantInfo.contact_email}</Text>
            </>
          )}
          {tenantInfo?.website_url && (
            <>
              <Text fontWeight="bold">ホームページ：</Text>
              <Text mb={2}>
                <a href={tenantInfo.website_url} target="_blank" rel="noopener noreferrer" style={{color: 'blue', textDecoration: 'underline'}}>
                  {tenantInfo.website_url}
                </a>
              </Text>
            </>
          )}
          {tenantInfo?.address && (
            <Box borderRadius="md" overflow="hidden" borderWidth={1} borderColor="gray.200">
              <iframe
                title="Google Map"
                width="100%"
                height="440"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${encodeURIComponent(tenantInfo?.address)}&output=embed`}
              />
            </Box>
          )}
        </Box>
        {/* 予約フォーム */}
        <Box bg="white" p={8} borderRadius="lg" boxShadow="lg">
          <Heading size="md" mb={6}>来店予約フォーム(代表者様の情報を入力してください)</Heading>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>お名前</FormLabel>
                <Input name="name" value={form.name} onChange={handleChange} placeholder="例: 山田 太郎" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>電話番号</FormLabel>
                <Input name="phone" value={form.phone} onChange={handleChange} placeholder="例: 09012345678" maxLength={11} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>メールアドレス</FormLabel>
                <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="email@example.com" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>住所</FormLabel>
                <Input name="address" value={form.address} onChange={handleChange} placeholder="都道府県・市区町村・番地" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>年齢</FormLabel>
                <Input name="age" value={form.age} onChange={handleChange} placeholder="例: 35" type="number" min={0} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>家族構成</FormLabel>
                <Input name="family" value={form.family} onChange={handleChange} placeholder="例: 夫婦＋子2人" />
              </FormControl>
              {[1,2,3].map((n) => (
                <Box key={n} borderWidth={1} borderRadius="md" p={3} bg="gray.50">
                  <FormLabel mb={1}>{`第${n}希望日時`}</FormLabel>
                  <Flex gap={2} align="center" direction={{ base: "column", md: "row" }}>
                    <Input 
                      name={`date${n}`} 
                      type="date" 
                      value={form[`date${n}`]} 
                      onChange={handleChange} 
                      w={{ base: "100%", md: "50%" }}
                      fontSize={{ base: "sm", md: "md" }}
                    />
                    <Select 
                      name={`slot${n}`} 
                      value={form[`slot${n}`]} 
                      onChange={handleChange} 
                      placeholder="時間帯を選択" 
                      w={{ base: "100%", md: "50%" }}
                      fontSize={{ base: "sm", md: "md" }}
                    >
                      {TIME_SLOTS.map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </Select>
                  </Flex>
                </Box>
              ))}
              <FormControl isRequired>
                <FormLabel>お問い合わせ内容</FormLabel>
                <Textarea name="inquiry" value={form.inquiry} onChange={handleChange} placeholder="ご質問・ご要望など" rows={4} />
              </FormControl>
              <Button type="submit" bg="black" color="white" isLoading={loading} w="full">送信</Button>
              <Button variant="ghost" onClick={() => navigate('/')} w="full">キャンセル</Button>
            </VStack>
          </form>
        </Box>
      </Container>
    </Box>
    )}
        </Box>
    </Flex>
  );
} 